import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Sticky horizontal scrollable category chip-bar.
 *
 * Props:
 *  - chips:     Array<{ value: string, label: string, count?: number, accent?: boolean, icon?: ReactNode, onClick?: () => void }>
 *               If onClick is set, the chip behaves as an action (e.g. the "In cart" chip) and is NOT
 *               eligible to be the "active" filter chip.
 *  - active:    Currently selected chip value (string)
 *  - onSelect:  (value: string) => void
 *  - testIdPrefix: string for data-testid generation (e.g. "booking-chip", "services-chip")
 *  - barTestId: string for data-testid on the outer container (e.g. "category-chip-bar")
 *  - sticky:    boolean (default true) - if true, applies sticky top positioning
 *  - className: extra outer classes (optional)
 */
export function CategoryChipBar({ chips, active, onSelect, testIdPrefix = "chip", barTestId = "category-chip-bar", sticky = true, className = "" }) {
    const scrollRef = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const updateArrows = () => {
        const el = scrollRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setCanLeft(el.scrollLeft > 4);
        setCanRight(el.scrollLeft < maxScroll - 4);
    };

    useEffect(() => {
        updateArrows();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener("scroll", updateArrows, { passive: true });
        window.addEventListener("resize", updateArrows);
        return () => {
            el.removeEventListener("scroll", updateArrows);
            window.removeEventListener("resize", updateArrows);
        };
    }, [chips]);

    const scrollBy = (delta) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: delta, behavior: "smooth" });
    };

    const stickyCls = sticky ? "sticky top-20 z-30 -mx-6 lg:-mx-12 px-6 lg:px-12 py-3 bg-[#FBF6F0]/85 backdrop-blur-md border-b border-[#6B1535]/10" : "";

    return (
        <div className={`${stickyCls} ${className}`} data-testid={barTestId}>
            <div className="relative">
                {canLeft && (
                    <button
                        type="button"
                        onClick={() => scrollBy(-240)}
                        data-testid={`${testIdPrefix}-scroll-left`}
                        aria-label="Scroll left"
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 flex items-center justify-center bg-[#FBF6F0]/95 border border-[#C29A5B]/40 text-[#8F622C] hover:bg-[#6B1535] hover:text-[#FFF8F2] transition-colors shadow-[0_0_12px_rgba(0,0,0,0.6)]"
                    >
                        <ChevronLeft size={14} />
                    </button>
                )}
                {canRight && (
                    <button
                        type="button"
                        onClick={() => scrollBy(240)}
                        data-testid={`${testIdPrefix}-scroll-right`}
                        aria-label="Scroll right"
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 flex items-center justify-center bg-[#FBF6F0]/95 border border-[#C29A5B]/40 text-[#8F622C] hover:bg-[#6B1535] hover:text-[#FFF8F2] transition-colors shadow-[0_0_12px_rgba(0,0,0,0.6)]"
                    >
                        <ChevronRight size={14} />
                    </button>
                )}
                <div
                    ref={scrollRef}
                    className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 scroll-smooth"
                    role="tablist"
                >
                    {chips.map((c) => {
                        const isAction = typeof c.onClick === "function";
                        const isActive = !isAction && c.value === active;
                        let cls;
                        if (c.accent) {
                            cls = "border-[#C29A5B] bg-[#6B1535]/[0.08] text-[#8F622C] hover:bg-[#6B1535]/[0.18]";
                        } else if (isActive) {
                            cls = "bg-[#6B1535] text-[#FFF8F2] border-[#C29A5B]";
                        } else {
                            cls = "border-[#C29A5B]/35 text-[#3D0C1F] hover:border-[#C29A5B] hover:text-[#8F622C]";
                        }
                        return (
                            <button
                                key={c.value}
                                type="button"
                                role={isAction ? undefined : "tab"}
                                aria-selected={isAction ? undefined : isActive}
                                onClick={() => (isAction ? c.onClick() : onSelect(c.value))}
                                data-testid={`${testIdPrefix}-${c.value}`}
                                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[0.65rem] tracking-[0.22em] uppercase border transition-colors whitespace-nowrap ${cls}`}
                            >
                                {c.icon}
                                <span>{c.label}</span>
                                {typeof c.count === "number" && (
                                    <span className={"text-[0.55rem] " + (isActive ? "opacity-70" : c.accent ? "" : "text-[#8A6574]")}>
                                        {c.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
